// The case-policy gate the navigational lifecycle opens before it keys
// anything for a connection, and the one object-key rule every keyed surface
// reads.
//
// The gate is TWO steps, because the two questions have two different scopes:
//
//   `ensureEngineCasePolicy` — per CONNECTION:
//     - mysql — `@@lower_case_table_names`. It decides the index's object keys
//       per kind and per name component (`mysqlCaseProbe.ts`), and a kind's
//       policy FREEZES at that kind's first record, so a build that starts
//       without it can fold `v` and `V` into one bucket permanently.
//     - mssql — the SERVER collation. It decides whether `Sales` and `sales`
//       are one database or two, i.e. which index and which References tab a
//       reader gets (`mssqlCollationProbe.ts`).
//     - pgsql / oracle / sqlite / kusto — nothing to learn: PostgreSQL and
//       Oracle store identifiers as spelled, SQLite compares case-insensitively
//       and is one file, and the other engines key nothing of their own.
//
//   `ensureDatabaseCasePolicy` — per DATABASE IDENTITY:
//     - mssql — that DATABASE's collation, which is what SQL Server compares
//       OBJECT and SCHEMA names in. A `_CI_` instance can hold a `_CS_`
//       database, so this cannot be answered once per connection. It runs
//       second because the identity key it files under depends on the server
//       collation the first step learns.
//     - every other engine — nothing per database to learn.
//
// Each probe is one row on the introspection lane and is skipped when the
// answer is already remembered, so the gate normally costs one query per
// connection plus one per database. That is the NORMAL path, not a bound: a
// MySQL connection whose paired probe is rejected runs a second, mode-only
// query (`navCasePolicy.test.ts` asserts the two), a SQL Server connection
// whose measured catalog-collation statement is refused at compile time falls
// back to a second, data-collation-only query, and an unconfigured connection
// on a multi-database engine also pays the once-per-connection login-default
// probe (`navLifecycleDatabaseResolution.ts`). A failed probe answers `false` — the
// lifecycle then builds nothing rather than keying an index on a guess, and
// the next request tries again.
//
// "Known" means REMEMBERED HERE, for the current generation
// (`casePolicyState.ts`) — never a value borrowed from a cache this layer does
// not own. MySQL's is the case that forces the rule: the synchronous accessor
// also reads `@@lower_case_table_names` off the IntelliSense schema snapshot,
// which carries no generation token and which DDL drops. A gate opened on that
// value keys an index whose remaining kinds then freeze on the folded default
// the moment the snapshot goes — and a kind's key policy cannot be repaired
// once frozen.

import type { DatabaseEngineId } from '@/core/engines';

import { readCasePolicy } from '../casePolicyState';
import type { ObjectKeyCase } from '../index/identifierIndex';
import {
  mssqlDatabaseNamesCaseSensitive,
  mssqlObjectNamesCaseSensitive,
  probeMssqlDatabaseCollation,
  probeMssqlServerCollation,
} from '../mssqlCollationProbe';
import { mysqlObjectKeyCase, probeMysqlLowerCaseTableNames } from '../mysqlCaseProbe';
import type { ObjectKind } from '../types';

import { databaseIdentityKey } from './databaseIdentity';

const EXACT: ObjectKeyCase = { schema: true, name: true };
const FOLDED: ObjectKeyCase = { schema: false, name: false };

/** Learn (or confirm) the CONNECTION's case policy. `true` = known. */
export async function ensureEngineCasePolicy(
  connectionId: string,
  engineId: DatabaseEngineId,
  database: string,
): Promise<boolean> {
  if (engineId === 'mysql') {
    await probeMysqlLowerCaseTableNames(connectionId, database);
    // "KNOWN" IS THE MODE, and only the mode. The same probe also brings back
    // `@@lower_case_file_system` and remembers it in the SAME record
    // (`casePolicyState.ts`'s `CasePolicyValues`) — that is a storage decision,
    // not a gate one. A server that answers `@@lower_case_table_names` but
    // rejects the paired shape (an exotic fork, a proxy that filters system
    // variables) still gets its index: the mode is what an object key freezes
    // on, the file system only re-decides a TRIGGER leaf and falls back to the
    // mode. Reading both here would cost such a connection every navigational
    // feature over a value nothing keys on.
    //
    // The REMEMBERED value only — never the synchronous accessor, which falls
    // back to the IntelliSense schema snapshot (`mysqlCaseProbe.ts`). That
    // snapshot belongs to `mysqlSchemaCache.ts`, carries no generation token,
    // and DDL drops it: a failed probe backed by one would open the gate, key
    // the kinds that record first, and then leave a kind that had not recorded
    // yet to freeze on the folded default once the snapshot is gone —
    // permanently, since a kind's key policy freezes at its first record.
    // `readCasePolicy` answers only for the CURRENT generation, so what it
    // returns is what `objectKeyCaseFor` will keep answering for this
    // connection's lifetime.
    return readCasePolicy(connectionId).mysqlLowerCaseTableNames !== undefined;
  }
  if (engineId === 'mssql') {
    await probeMssqlServerCollation(connectionId, database);
    return mssqlDatabaseNamesCaseSensitive(connectionId) !== undefined;
  }
  return true;
}

/** Learn (or confirm) one DATABASE's case policy. `true` = known. Call only
 *  after {@link ensureEngineCasePolicy} has answered `true`: on SQL Server the
 *  identity this files under is computed from the server collation. */
export async function ensureDatabaseCasePolicy(
  connectionId: string,
  engineId: DatabaseEngineId,
  database: string,
): Promise<boolean> {
  if (engineId !== 'mssql') return true;
  const identity = databaseIdentityKey(connectionId, engineId, database);
  await probeMssqlDatabaseCollation(connectionId, database, identity);
  return mssqlObjectNamesCaseSensitive(connectionId, identity) !== undefined;
}

/**
 * How this connection's `database` tells `v` from `V` when it keys an OBJECT,
 * per object kind and per name component — the one rule behind the identifier
 * index's object keys (`identifierIndex.ts`), the DDL invalidator's candidate
 * dedupe (`indexInvalidator.ts`) and find-references / rename grouping
 * (`findReferencesService.ts`, `rename/refGrouper.ts`). Keying any of those
 * differently from the others merges (or splits) objects the rest keep apart.
 *
 * `undefined` from the resolver means "not known yet"; every consumer folds
 * then, which is the safe, over-inclusive default. The lifecycle's gate makes
 * sure a live index never sees it.
 */
export function objectKeyCaseFor(
  connectionId: string,
  engineId: DatabaseEngineId,
  database: string,
): (kind: ObjectKind) => ObjectKeyCase | undefined {
  // PostgreSQL and Oracle store a delimited identifier exactly as spelled, so
  // `public.v` and `public."V"` are two objects whatever the server says.
  if (engineId === 'pgsql' || engineId === 'oracle') return () => EXACT;
  // MySQL decides per kind AND per component from `@@lower_case_table_names`.
  if (engineId === 'mysql') return (kind) => mysqlObjectKeyCase(connectionId, kind);
  if (engineId === 'mssql') {
    // SQL Server compares object AND schema names in the DATABASE's collation.
    const caseSensitive = mssqlObjectNamesCaseSensitive(
      connectionId,
      databaseIdentityKey(connectionId, engineId, database),
    );
    if (caseSensitive === undefined) return () => undefined;
    return () => (caseSensitive ? EXACT : FOLDED);
  }
  // SQLite compares case-insensitively; kusto and mock key nothing of their own.
  return () => FOLDED;
}
